<template>
  <div style='position: relative'>
    <b-loading :active='items === null' :is-full-page='false' />

    <div class='actions'>
      <div class='search'>
        <b-field style='width: 400px'>
          <b-input v-model='query' :placeholder="$t('questions.search1')" icon='search' size='is-small' expanded
            :disabled='!items || items.length === 0' />
          <button v-if='query' class='button is-small' @click="query = ''">
            <b-icon icon='times-circle' size='is-small' />
            <span>{{$t('questions.search2')}}</span>
          </button>
        </b-field>
      </div>

      <div class='export-csv'>
        <button class='button is-small' @click='exportCSV(filteredItems)'>
          <b-icon icon='arrow-down' size='is-small' />
          <span>{{$t('questions.export-csv')}}</span>
        </button>
      </div>
      <div class='poser-question' v-if='site'>
        <button class='button is-small' @click='showModal = true'>
          <b-icon icon='plus' size='is-small' />
          <span>{{$t('questions.poser-question')}}</span>
        </button>
      </div>
    </div>

    <div class='include-checkboxes'>
      <b-checkbox v-if='site' v-model='includeSite' size='is-small'>
        {{$t('questions.include-checkboxes')}}<em v-text='site.name' />
      </b-checkbox>
      &nbsp;&nbsp;
      <b-checkbox v-if='portefeuille' v-model='includePortefeuille' size='is-small'>
        {{$t('questions.include-checkboxes')}}<em v-text='portefeuille.name' />
      </b-checkbox>
    </div>

    <div v-if='query'>
      <h1 class='is-size-4'>{{$t('questions.query1')}}</h1>

      <b-message v-if='filteredItems.length === 0'>
        {{$t('questions.query2')}}
      </b-message>
    </div>

    <b-message v-if='questionsDenied' type='is-warning'>
      {{$t('questions.denied')}}
    </b-message>

    <b-message v-if='!questionsDenied && items && items.length === 0'>
      {{$t('questions.empty')}}
    </b-message>

    <b-table :data='filteredItems' :mobile-cards='false' :default-sort="['date', 'desc']" detailed>
      <template slot-scope='props'>

        <b-table-column field='chrono' :label="$t('questions.labelchrono')" sortable>
          {{ props.row.chrono }}
        </b-table-column>
        <b-table-column field='title' :label="$t('questions.labelobjet')" sortable>
          {{ props.row.title }}
        </b-table-column>

        <b-table-column field='date' :label="$t('questions.labeldate')" sortable>
          {{ props.row.date | moment('DD/MM/YYYY') }}
        </b-table-column>

        <b-table-column field='status' :label="$t('questions.labeletat')" sortable :custom-sort='sortByStatus'>
          <div class='question-status'>
            {{ props.row.workflowState }}
          </div>
        </b-table-column>

        <b-table-column field='bien' :label="$t('questions.labelbien')" sortable>
          {{ bienName(props.row.bien) }}
        </b-table-column>

        <b-table-column field='site' :label="$t('questions.labelsite')" sortable>
          {{ siteName(props.row.bien) }}
        </b-table-column>

        <b-table-column field='theme' :label="$t('questions.labeltheme')" sortable :custom-sort='sortByTheme'>
          {{ props.row.theme.name }}
        </b-table-column>

        <b-table-column field='auteur' :label="$t('questions.labelauteur')" sortable>
          {{ props.row.questionAuteur.name }}
        </b-table-column>

      </template>

      <template slot='detail' slot-scope='props'>
        <p class='info'>{{$t('questions.poseele')}} {{ props.row.date | moment('DD/MM/YYYY') }}</p>
        <!-- Nom du document tel que la data room l'affiche ; absent si la question ne porte
             sur aucun document, ou si le document a ete supprime. -->
        <p v-if='props.row.document' class='info'>
          {{ $t('questions.surdocument') }} <em>{{ props.row.document.name }}</em>
        </p>
        <p class='question'>
          <strong>{{ props.row.question }}</strong>
        </p>
        <div v-if='props.row.reponse && props.row.reponseAuteur' class='reponse'>
          <p class='info'>
            {{$t('questions.reponsele')}}
            {{ props.row.reponseDate | moment('DD/MM/YYYY') }} par
            <em>{{ props.row.reponseAuteur.name }}</em>
          </p>
          <p class='reponse'>
            <strong>{{ props.row.reponse }}</strong>
          </p>
        </div>
        <div v-if='props.row.complement && props.row.complementAuteur' class='reponse'>
          <p class='info'>
            {{$t('questions.complementle')}}
            {{ props.row.complementDate | moment('DD/MM/YYYY') }} par
            <em>{{ props.row.complementAuteur.name }}</em>
          </p>
          <p class='reponse'>
            <strong>{{ props.row.complement }}</strong>
          </p>
        </div>
      </template>
    </b-table>

    <b-modal :active.sync='showModal' has-modal-card>
      <PortefeuilleQuestionModal @sent='getData' />
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import PortefeuilleQuestionModal from '@/components/Portefeuille/QuestionModal'

export default {
  props: {
    includePortefeuilleData: Boolean,
  },
  components: {
    PortefeuilleQuestionModal,
  },
  data () {
    return {
      query: '',
      showModal: false,
      includeSite: false,
      includePortefeuille: this.includePortefeuilleData || false,
    }
  },
  computed: {
    ...mapState({
      biens: (state) => state.inv.biens,
      questionsDenied: (state) => state.inv.questionsDenied,
    }),
    items () {
      if (!this.$store.state.inv.questions) return null

      if (!this.includeSite && !this.includePortefeuille) {
        return this.$store.state.inv.questions.filter(
          (i) => i.bien === +this.$route.params.bien
        )
      }

      if (this.includeSite) {
        const bienIds = this.biens
          .filter((i) => i.site === +this.$route.params.site)
          .map((i) => i.id)
        return this.$store.state.inv.questions.filter(
          (i) => bienIds.indexOf(i.bien) !== -1
        )
      }

      return this.$store.state.inv.questions
    },
    filteredItems () {
      if (!this.items || this.items.length === 0) return []
      if (!this.query) return this.items

      const reg = new RegExp(
        '' + this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'i'
      )

      // Le bloc « filters » est retiré : this.filters n'a jamais existé dans ce
      // composant (Object.keys(undefined) levait un TypeError à la première frappe),
      // et son propre commentaire le disait inutile (reliquat d'un filtre par colonne).
      //
      // Plus de ligne « placeholder » en l'absence de résultat : elle faisait planter
      // le rendu (date/theme absents) et neutralisait le message « aucun résultat »
      // du template, qui teste filteredItems.length === 0.
      return this.items.filter((item) => {
        if (item.title && item.title.match(reg)) return true
        if (item.chrono && item.chrono.match(reg)) return true
        if (item.question && item.question.match(reg)) return true
        if (item.reponse && item.reponse.match(reg)) return true
        if (item.complement && item.complement.match(reg)) return true
        if (item.date && item.date.match(reg)) return true
        if (item.questionAuteur && item.questionAuteur.name && item.questionAuteur.name.match(reg)) return true
        if (item.theme && item.theme.name && item.theme.name.match(reg)) return true
        if (item.document && item.document.name && item.document.name.match(reg)) return true
        return false
      })
    },
    portefeuille () {
      if (!this.$route.params.portefeuille) return
      const value = this.biens.find(
        (i) => i.portefeuille === +this.$route.params.portefeuille
      )
      return value
        ? {
          id: value.portefeuille,
          name: value.portefeuilleName,
        }
        : null
    },
    site () {
      if (!this.$route.params.site) return
      const value = this.biens.find((i) => i.site === +this.$route.params.site)
      return value
        ? {
          id: value.site,
          name: value.siteName,
        }
        : null
    },
  },
  watch: {
    '$route.params.bien' () {
      this.query = ''
    },
  },
  created () {
    window.addEventListener('refreshQuestions', this.getData)
  },
  destroyed () {
    window.removeEventListener('refreshQuestions', this.getData)
  },
  methods: {
    async getData () {
      try {
        await this.$store.dispatch('inv/getQuestions', this.$route.params.portefeuille)
      } catch (e) {
        // Le refus est deja porte par le store et affiche dans la vue : le toast ne
        // sert qu'aux autres erreurs, pour ne pas doubler le message.
        if (!this.questionsDenied) {
          this.apiError(e)
        }
      }
    },
    bienName (id) {
      const item = _.find(this.biens, { id })
      return item ? item.name : null
    },
    siteName (id) {
      const item = _.find(this.biens, { id })
      return item ? item.siteName : null
    },
    sortByStatus (a, b, isAsc) {
      if (isAsc) {
        return a.workflowState.localeCompare(b.workflowState)
      } else {
        return b.workflowState.localeCompare(a.workflowState)
      }
    },
    sortByTheme (a, b, isAsc) {
      if (isAsc) {
        return a.theme.name.localeCompare(b.theme.name)
      } else {
        return b.theme.name.localeCompare(a.theme.name)
      }
    },
    exportCSV (data) {
      let csv = ''

      // Document en dernier : les colonnes existantes gardent leur place.
      const fieldNames = ['id', 'chrono', 'question', 'workflowState', 'bien', 'site', 'theme', 'questionAuteur', 'reponse', 'reponseDate', 'reponseAuteur', 'document']

      const header = 'QUESTION;CHRONO QUESTION;OBJET;ETAT;BIEN;SITE;THEME;AUTEUR(QUESTION);REPONSE;REPONSEDATE;AUTEUR(REPONSE);DOCUMENT\n'
      csv += header

      data.forEach(obj => {
        let site = ''

        const row = fieldNames.map(key => {
          let value = obj[key]

          if (key === 'bien') {
            value = this.bienName(value)
            site = this.siteName(value)
          }

          if (key === 'site') {
            value = site
          }

          if (key === 'theme' && value !== null && value !== undefined) {
            value = value.name
          }

          if (key === 'questionAuteur' && value !== null && value !== undefined) {
            value = value.name
          }

          if (key === 'reponseAuteur' && value !== null && value !== undefined) {
            console.log(value)
            value = value.name
          }

          if (key === 'document' && value !== null && value !== undefined) {
            value = value.name
          }

          if (typeof value === 'string' && value.includes(';')) {
            return `"${value}"` // Enclose in double quotes if value contains a comma
          }
          return value
        }).join(';')

        csv += row + '\n'
      })

      const anchor = document.createElement('a')
      anchor.href = 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(csv)
      anchor.target = '_blank'
      anchor.download = 'exportCSV.csv'
      anchor.click()
    },
  },
}
</script>

<style lang='scss' scoped>
h1 {
  margin: 1.5em 0 0.5em;
}

.info {
  font-size: 0.85em;
  margin-bottom: 0.25em;
}

p.question {
  margin-bottom: 0.5em;
}

div.reponse {
  border-top: 1px solid #f5f5f5;
  padding: 1em 0;
}

p.question,
p.reponse {
  white-space: pre-line;
}

.icon {
  opacity: 0.5;
  margin-right: 0.25em;
  font-size: 0.8em;
}

.question-status {
  position: relative;
  font-size: 0.8em;
  padding-left: 20px;
  padding-top: 5px;
  line-height: 1.2;

  .icon {
    position: absolute;
    top: 4px;
    left: 0;
  }
}

.include-checkboxes {
  float: right;
  margin: 0.5em 0 0.25em;
}
</style>
